import * as Sentry from '@sentry/react';
import type { ProfileLoadFailure, SignInDiagnostics, User } from './authService';
import type { SignInAttempt, SignInStage } from './resolveSignInUser';

/** Where the profile load ran: signing in (or restoring a session), a retry from the "couldn't
 *  load your account" screen, or a later refresh. */
export type ProfileLoadPhase = 'sign_in' | 'account_retry' | 'permissions_refresh' | 'profile_page_refresh';

/** Claim names from the token the user was built from. Never their values. */
const claimNamesOf = (user: User | undefined): string[] => Object.keys(user?.claims?.['https://hasura.io/jwt/claims'] ?? {}).sort();

const errorFields = (error: unknown) => ({
  errorName: error instanceof Error ? error.name : error === undefined ? undefined : typeof error,
  errorMessage: error instanceof Error ? error.message : undefined,
});

/**
 * The Sentry event for a signed-in Firebase user whose HopDrive profile did not load. Before
 * this, the portal stored the user with `profile: null` and showed "Unknown User" and no
 * products, and the only trace was a console line.
 *
 * Grouped on the stable message `dealer.profile_load_failed`. The tags say where and why:
 * - `stage`: `firebase_token` (Firebase gave no usable token, so our database was never asked)
 *   or `profile_query` (the token was fine and the `getUserProfile` request failed).
 * - `failure_kind`, plus `gql_error_code` (Hasura's `extensions.code`, such as `invalid-jwt`),
 *   `http_status` and `timed_out` when they apply, so reports group by cause.
 *
 * The extras carry the evidence for each stage: `token` (source, issued, expires, auth time,
 * sign-in provider, claim names), `query` (operation, variables, endpoint host, duration, and
 * Hasura's answer or the first 500 characters of a non-JSON body), and `attemptHistory`. Never
 * the token, a claim value, an email, or a successful answer.
 */
export function reportProfileLoadFailure(input: {
  phase: ProfileLoadPhase;
  user: User | undefined;
  failure: ProfileLoadFailure | undefined;
  firebaseUid: string | undefined;
  attempts: number;
  /** What the portal did about it: blocked the portal behind the account screen, or kept the
   *  user it already had. */
  action: 'blocked' | 'kept_existing_user';
  /** A throw outside the profile request itself (reading the token, building the user). */
  error?: unknown;
  stage?: SignInStage;
  diagnostics?: SignInDiagnostics;
  history?: SignInAttempt[];
}): void {
  const { phase, user, failure, firebaseUid, attempts, action, error, diagnostics, history } = input;
  // A throw is what ended the attempt, so it wins over any profile failure passed with it.
  const threw = error !== undefined;
  const failureError = threw ? error : failure?.kind === 'network_error' ? failure.error : undefined;
  const { errorName } = errorFields(failureError);
  const errorMessage =
    !threw && failure?.kind === 'graphql_error' ? failure.message : failureError instanceof Error ? failureError.message : undefined;
  const stage = input.stage ?? (threw ? 'unknown' : 'profile_query');

  const tags: Record<string, string> = {
    feature: 'auth',
    phase,
    stage,
    failure_kind: threw ? 'threw' : (failure?.kind ?? 'unknown'),
    role: user?.claims?.['x-hasura-default-role'] ?? 'none',
    action,
  };
  if (diagnostics?.token) tags.token_source = diagnostics.token.source;
  if (!threw && failure?.kind === 'graphql_error') tags.gql_error_code = failure.code ?? 'none';
  if (!threw && failure?.kind === 'http_error') tags.http_status = String(failure.status);
  if (!threw && failure?.kind === 'network_error') tags.timed_out = String(failure.timedOut);

  Sentry.captureMessage('dealer.profile_load_failed', {
    level: 'error',
    tags,
    extra: {
      firebaseUid,
      userIdClaim: user?.claims?.['x-hasura-user-id'] ?? null,
      customerIdClaim: user?.claims?.['x-hasura-customer-id'] ?? null,
      claimNames: claimNamesOf(user),
      attempts,
      // A profile failure passed alongside a throw, kept so it is not lost.
      earlierFailureKind: threw ? failure?.kind : undefined,
      contentType: failure?.kind === 'http_error' ? failure.contentType : undefined,
      errorName,
      errorMessage,
      token: diagnostics?.token,
      query: diagnostics?.query,
      attemptHistory: history,
    },
  });
}

/**
 * The Sentry event for a Firebase user whose token's role may not use the portal: either a
 * role outside the portal's list or no HopDrive claims at all (a token minted before the
 * claims were set, or a claims job that failed). The user is told and signed out.
 */
export function reportRoleRejected(input: { user: User | undefined; firebaseUid: string | undefined; diagnostics?: SignInDiagnostics }): void {
  const { user, firebaseUid, diagnostics } = input;
  const claimNames = claimNamesOf(user);
  Sentry.captureMessage('dealer.role_rejected', {
    level: 'warning',
    tags: {
      feature: 'auth',
      // The role is a fixed vocabulary, not personal data; `none` means the claim is absent.
      role: user?.claims?.['x-hasura-default-role'] ?? 'none',
      has_hasura_claims: String(claimNames.length > 0),
      ...(diagnostics?.token ? { token_source: diagnostics.token.source } : {}),
    },
    extra: {
      firebaseUid,
      userIdClaim: user?.claims?.['x-hasura-user-id'] ?? null,
      claimNames,
      token: diagnostics?.token,
    },
  });
}

/**
 * The Sentry event for a working rooftop that did not load after the profile did. Without a
 * rooftop the portal has no customer, so it shows no products. `source` is the request that
 * failed: the default rooftop for a dealer (`default_rooftop`, `GetDefaultRooftop` by
 * customer id) or the header's rooftop list for a power role (`header_rooftops`).
 */
export function reportRooftopLoadFailed(input: {
  source: 'default_rooftop' | 'header_rooftops';
  /** Why there is no rooftop: the request failed, or it answered with nothing. */
  outcome: 'request_failed' | 'empty';
  operation: string;
  variables: Record<string, unknown>;
  role?: string | null;
  userId?: number | null;
  /** Where the customer id came from, for the default rooftop: the pinned rooftop or the profile. */
  customerIdSource?: 'pinned' | 'profile';
  error?: unknown;
  /** GraphQL errors from Apollo, as message and code. */
  graphQLErrors?: Array<{ message?: string; code?: unknown }>;
  networkStatus?: number;
}): void {
  const { source, outcome, operation, variables, role, userId, customerIdSource, error, graphQLErrors, networkStatus } = input;
  Sentry.captureMessage('dealer.rooftop_load_failed', {
    level: 'error',
    tags: {
      feature: 'auth',
      source,
      outcome,
      role: role ?? 'none',
      ...(graphQLErrors?.[0]?.code ? { gql_error_code: String(graphQLErrors[0].code) } : {}),
      ...(networkStatus ? { http_status: String(networkStatus) } : {}),
    },
    extra: {
      operation,
      variables,
      userId: userId ?? null,
      customerIdSource,
      graphQLErrors,
      ...errorFields(error),
    },
  });
}
